import { Injectable } from "@angular/core";
import { ComponentStore } from "@ngrx/component-store";
import { Observable } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { CreateNewsletterTemplateState } from "./create-newsletter-template.state";
import { CreateNewsletterTemplateModel } from "src/app/models/create-newsletter-template-model";
import { NewsletterTemplateModel } from "src/app/models/newsletter-template-model";
import { NewsletterTemplateService } from "src/app/services/newsletter-template.service";

const initialState: CreateNewsletterTemplateState = {
    loading: false,
    loaded: false,
    success: false,
    errorMessage: null,
    createNewsletterTemplate: null,
    newsletterTemplate: null,
};

@Injectable({
    providedIn: 'root'
})
export class CreateNewsletterTemplateComponentStore extends ComponentStore<CreateNewsletterTemplateState> {

    constructor(private readonly newsletterTemplateService: NewsletterTemplateService) {
        super(initialState);
    }

    readonly createNewsletterTemplate$: Observable<CreateNewsletterTemplateModel> = this.select(state => state.createNewsletterTemplate);
    readonly newsletterTemplate$: Observable<NewsletterTemplateModel> = this.select(state => state.newsletterTemplate);
    readonly loading$: Observable<boolean> = this.select(state => state.loading);
    readonly loaded$: Observable<boolean> = this.select(state => state.loaded);
    readonly success$: Observable<boolean> = this.select(state => state.success);
    readonly errorMessage$: Observable<any> = this.select(state => state.errorMessage);

    readonly viewModel$ = this.select(
        this.createNewsletterTemplate$,
        this.newsletterTemplate$,
        this.loaded$,
        this.loading$,
        this.success$,
        this.errorMessage$,
        (createCampaignModel, campaign, loaded, loading, success, errorMessage) => ({
            createCampaignModel, campaign
            , loaded, loading, success, errorMessage
        })
    );

    readonly setInitial = this.updater(( _ : CreateNewsletterTemplateState) => {
        return {
            ...initialState
        };
    });

    readonly setLoading = this.updater((state: CreateNewsletterTemplateState) => {
        return {
            ...state,
            loading: true,
            loaded: false,
        };
    });

    readonly setLoaded = this.updater((state: CreateNewsletterTemplateState) => {
        return {
            ...state,
            loading: false,
            loaded: true,
        };
    });

    readonly updateCreateNewsletterTemplateState = this.updater((state: CreateNewsletterTemplateState, value: {
        createNewsletterTemplate: CreateNewsletterTemplateModel
    }) => {
        return {
            ...state,
            loading: false,
            loaded: true,
            success: true,
            createNewsletterTemplate: value.createNewsletterTemplate
        };
    });

    readonly updateNewsletterTemplateState = this.updater((state: CreateNewsletterTemplateState, value: {
        newsletterTemplate: NewsletterTemplateModel
    }) => {
        return {
            ...state,
            loading: false,
            loaded: true,
            success: true,
            newsletterTemplate: value.newsletterTemplate
        };
    });

    readonly setError = this.updater((state: CreateNewsletterTemplateState, value: {
        errorMessage: String
    }) => {
        return {
            ...state,
            loading: false,
            loaded: true,
            success: false,
            errorMessage: value.errorMessage,
        };
    });

    readonly createNewsletterTemplate = this.effect((createNewsletterTemplateParams$: Observable<{ companyId: string, createNewsletterTemplate: CreateNewsletterTemplateModel }>) => {
        this.setLoading();
        return createNewsletterTemplateParams$.pipe(
            switchMap((params => this.newsletterTemplateService.createNewsletterTemplate(params.companyId, params.createNewsletterTemplate).pipe(
                map((newsletterTemplate) => {
                    if(newsletterTemplate === null || newsletterTemplate === undefined){
                        this.setError({ errorMessage: "Can't create a new newsletter template." });
                        this.setLoaded();
                        this.setInitial();
                    }
                    else {
                        this.updateNewsletterTemplateState({ newsletterTemplate: newsletterTemplate });
                        this.updateCreateNewsletterTemplateState({createNewsletterTemplate : params.createNewsletterTemplate })
                        this.setLoaded();
                        this.setInitial();
                    }
                }),
                catchError(async (error) => {
                    this.setError({ errorMessage: error });
                    this.setLoaded();
                    this.setInitial();
                })
            ))
            )
        );
    });
}